// components/ora/Chat/Companion/chatScrollIntent.ts
//
// What the thread should do when its content or its container changes size
// (ORA-599, reworked for ORA-628). Kept apart from `ChatThread` so the
// decision can be tested as a table rather than through layout: jsdom has no
// geometry of its own, and the interesting cases here are all geometry.
//
// The model the reader feels is the one they know from other assistants:
//
// - Their question scrolls to the top of the thread and the answer streams
//   into the room below it. The thread does not follow the answer down; the
//   question stays put until the reader scrolls on their own.
// - Whenever there is content below the fold — an answer that has outgrown
//   the room, or a thread they scrolled up in — an arrow offers the bottom.
// - A reader at the bottom is followed by what arrives, as before.

/**
 * How close to the bottom counts as "following the answer". Past this the
 * reader has scrolled up to re-read something and the thread leaves them
 * there (ORA-459).
 */
export const FOLLOW_SCROLL_THRESHOLD_PX = 120;

/**
 * How much content below the fold is worth the arrow. Smaller than the follow
 * threshold on purpose: the arrow is a signal that there is more, not a
 * promise that the thread will not follow.
 */
export const MORE_BELOW_THRESHOLD_PX = 24;

/**
 * How long a scroll the thread started keeps the container's resize observer
 * quiet, in ms. A scroll changes layout — a scrollbar appears, sticky chrome
 * reflows — and that resize would otherwise be read as the keyboard opening
 * and scroll again. One frame is enough in a real browser; this leaves room
 * for a slow one, and for a smooth scroll's animation.
 */
export const SELF_SCROLL_SETTLE_MS = 150;

/**
 * The settle for a smooth scroll to a sent question, which can travel the
 * whole thread. Every position it passes through fires a scroll event, and
 * none of those is the reader choosing to leave the question.
 */
export const ANCHOR_SCROLL_SETTLE_MS = 600;

export interface ChatScrollGeometry {
  scrollTop: number;
  clientHeight: number;
  scrollHeight: number;
}

/** Pixels of content below the fold. Zero once the live edge is on screen. */
export const distanceFromBottom = ({
  scrollTop,
  clientHeight,
  scrollHeight,
}: ChatScrollGeometry): number => scrollHeight - scrollTop - clientHeight;

/** `scrollTop + clientHeight >= scrollHeight - threshold`, written out. */
export const isAtBottom = (
  geometry: ChatScrollGeometry,
  threshold: number = FOLLOW_SCROLL_THRESHOLD_PX
): boolean => distanceFromBottom(geometry) <= threshold;

/** Whether there is enough below the fold to offer the arrow for. */
export const hasMoreBelow = (geometry: ChatScrollGeometry): boolean =>
  distanceFromBottom(geometry) > MORE_BELOW_THRESHOLD_PX;

export type ChatScrollReason =
  /** The list itself got taller: streamed text, a block, a disclosure. */
  | 'content-growth'
  /**
   * The scroll container got shorter without the content changing — the
   * on-screen keyboard opening under the composer, most of the time.
   */
  | 'viewport-shrink';

export type ChatScrollMode =
  /**
   * The reader's latest question is pinned to the top of the thread and the
   * answer is filling the room under it. Nothing the thread does moves them;
   * only their own scroll ends this.
   */
  | 'anchored'
  /** The reader is wherever they last scrolled to. */
  | 'free';

export interface ChatScrollIntentInput {
  reason: ChatScrollReason;
  mode: ChatScrollMode;
  /**
   * Whether the reader was at the live edge before this change. For growth
   * this is read from the container there and then; for a shrinking container
   * it is what the last scroll said, because the new geometry already counts
   * the keyboard.
   */
  wasAtBottom: boolean;
}

export interface ChatScrollIntent {
  /** Put the live edge back on screen. */
  shouldScrollToBottom: boolean;
}

/**
 * The whole follow decision, as one pure function.
 *
 * Anchored, the thread never moves the reader: the question they asked stays
 * where they can see it and the answer grows below. Free, content that arrives
 * while they are at the bottom follows them down, and content that arrives
 * while they are up re-reading never moves them. Whether the arrow shows is a
 * separate question, answered from geometry alone (`hasMoreBelow`).
 */
export const computeScrollIntent = ({
  mode,
  wasAtBottom,
}: ChatScrollIntentInput): ChatScrollIntent => {
  if (mode === 'anchored') return { shouldScrollToBottom: false };

  return { shouldScrollToBottom: wasAtBottom };
};

export interface AnchorRoomInput {
  /** The scroll container's visible height, padding included. */
  clientHeight: number;
  paddingTop: number;
  paddingBottom: number;
  /**
   * From the top of the anchored question to the end of the real content, in
   * layout pixels (`offsetTop` differences, never client rects: a minimized
   * shell scales its window, and the room must not be sized through that).
   */
  occupied: number;
}

/**
 * How much room to leave under the latest turn so its question can sit at the
 * top of the thread: the visible height, less the container's padding, less
 * what the turn already fills. Shrinks as the answer grows and reaches zero
 * once the answer alone fills the view.
 */
export const anchorRoomHeight = ({
  clientHeight,
  paddingTop,
  paddingBottom,
  occupied,
}: AnchorRoomInput): number =>
  Math.max(0, clientHeight - paddingTop - paddingBottom - occupied);

export interface AnchorScrollInput {
  /** The anchored question's `offsetTop`. */
  anchorTop: number;
  /** The list's `offsetTop`, against the same offset parent. */
  listTop: number;
}

/**
 * The `scrollTop` that puts the anchored question at the top of the thread,
 * under the container's own top padding: the question's distance into the
 * list, since the list starts exactly one top padding into the container.
 */
export const anchorScrollTop = ({
  anchorTop,
  listTop,
}: AnchorScrollInput): number => Math.max(0, anchorTop - listTop);
